import {
  ToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  ToggleButtonGroupProps as MuiToggleButtonGroupProps,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { ELEVATION_CONSTANTS } from "../../constants/styles/dropShadow";
import { COLOR_CONSTANTS } from "../../constants/styles/color";

interface ToggleButtonGroupProps extends MuiToggleButtonGroupProps {
  children: ReactNode;
  size?: "small" | "medium" | "large";
  selectionMode?: "single" | "multiple";
  hasDropshadow?: boolean;
}

export default function ToggleButtonGroup({
  children,
  size = "small",
  selectionMode = "single",
  value,
  onChange,
  hasDropshadow = true,
  ...props
}: ToggleButtonGroupProps) {
  const [internalValue, setInternalValue] = useState<string | string[] | null>(
    selectionMode === "multiple" ? [] : null
  );

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: string | string[] | null
  ) => {
    if (onChange) {
      onChange(event, newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  const currentValue = value !== undefined ? value : internalValue;
  const isVertical = props.orientation === "vertical";
  function sizeStyles() {
    switch (size) {
      case "small":
        return {
          boxShadow: hasDropshadow ? ELEVATION_CONSTANTS[4] : "none",
          "& .MuiToggleButton-root": {
            padding: "6px",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: "none",
            backgroundColor: "#ffffff",
            color: COLOR_CONSTANTS.COLOR.BLUE_GREY[700],
            "&:not(:first-of-type)": {
              borderLeft: isVertical ? "none" : "1px solid rgba(0, 0, 0, 0.12)",
              marginLeft: isVertical ? 0 : "-1px",
              borderTop: isVertical ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
              marginTop: isVertical ? "-1px" : 0,
            },
            "&:first-of-type": {
              borderTopLeftRadius: isVertical ? "4px" : "4px",
              borderTopRightRadius: isVertical ? "4px" : 0,
              borderBottomLeftRadius: isVertical ? 0 : "4px",
              borderBottomRightRadius: 0,
            },
            "&:last-of-type": {
              borderTopRightRadius: isVertical ? 0 : "4px",
              borderBottomRightRadius: isVertical ? "4px" : "4px",
              borderBottomLeftRadius: isVertical ? "4px" : 0,
              borderTopLeftRadius: 0,
            },
            "&:only-child": { borderRadius: "4px" },
            "&.Mui-selected": {
              backgroundColor: COLOR_CONSTANTS.primary._states.selected2,
              color: COLOR_CONSTANTS.common.white_states.main,
              "&:hover": {
                backgroundColor: COLOR_CONSTANTS.primary._states.selected2,
                color: COLOR_CONSTANTS.common.white_states.main,
              },
            },
            "&:hover": {
              backgroundColor: "#ffffff",
              color: COLOR_CONSTANTS.COLOR.BLUE_GREY[700],
              boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.1)",
            },
          },
        };
      case "medium":
        return {
          boxShadow: hasDropshadow ? ELEVATION_CONSTANTS[4] : "none",
          "& .MuiToggleButton-root": {
            padding: "6px",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: "none",
            backgroundColor: "#ffffff",
            color: COLOR_CONSTANTS.COLOR.BLUE_GREY[700],
            "&:not(:first-of-type)": {
              borderLeft: isVertical ? "none" : "1px solid rgba(0, 0, 0, 0.12)",
              marginLeft: isVertical ? 0 : "-1px",
              borderTop: isVertical ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
              marginTop: isVertical ? "-1px" : 0,
            },
            "&:first-of-type": {
              borderTopLeftRadius: isVertical ? "4px" : "4px",
              borderTopRightRadius: isVertical ? "4px" : 0,
              borderBottomLeftRadius: isVertical ? 0 : "4px",
              borderBottomRightRadius: 0,
            },
            "&:last-of-type": {
              borderTopRightRadius: isVertical ? 0 : "4px",
              borderBottomRightRadius: isVertical ? "4px" : "4px",
              borderBottomLeftRadius: isVertical ? "4px" : 0,
              borderTopLeftRadius: 0,
            },
            "&:only-child": { borderRadius: "4px" },
            "&.Mui-selected": {
              backgroundColor: COLOR_CONSTANTS.primary._states.selected2,
              color: COLOR_CONSTANTS.common.white_states.main,
              "&:hover": {
                backgroundColor: COLOR_CONSTANTS.primary._states.selected2,
                color: COLOR_CONSTANTS.common.white_states.main,
              },
            },
            "&:hover": {
              backgroundColor: "#ffffff",
              color: COLOR_CONSTANTS.COLOR.BLUE_GREY[700],
              boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.1)",
            },
          },
        };
      case "large":
        return {
          boxShadow: hasDropshadow ? ELEVATION_CONSTANTS[4] : "none",
          "& .MuiToggleButton-root": {
            padding: "16px",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: "none",
            backgroundColor: "#ffffff",
            color: COLOR_CONSTANTS.COLOR.BLUE_GREY[700],
            "&:not(:first-of-type)": {
              borderLeft: isVertical ? "none" : "1px solid rgba(0, 0, 0, 0.12)",
              marginLeft: isVertical ? 0 : "-1px",
              borderTop: isVertical ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
              marginTop: isVertical ? "-1px" : 0,
            },
            "&:first-of-type": {
              borderTopLeftRadius: isVertical ? "4px" : "4px",
              borderTopRightRadius: isVertical ? "4px" : 0,
              borderBottomLeftRadius: isVertical ? 0 : "4px",
              borderBottomRightRadius: 0,
            },
            "&:last-of-type": {
              borderTopRightRadius: isVertical ? 0 : "4px",
              borderBottomRightRadius: isVertical ? "4px" : "4px",
              borderBottomLeftRadius: isVertical ? "4px" : 0,
              borderTopLeftRadius: 0,
            },
            "&:only-child": { borderRadius: "4px" },
            "&.Mui-selected": {
              backgroundColor: COLOR_CONSTANTS.primary._states.selected2,
              color: COLOR_CONSTANTS.common.white_states.main,
              "&:hover": {
                backgroundColor: COLOR_CONSTANTS.primary._states.selected2,
                color: COLOR_CONSTANTS.common.white_states.main,
              },
            },
            "&:hover": {
              backgroundColor: "#ffffff",
              color: COLOR_CONSTANTS.COLOR.BLUE_GREY[700],
              boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.1)",
            },
          },
        };
    }
  }
  return (
    <MuiToggleButtonGroup
      sx={{ ...sizeStyles() }}
      value={currentValue}
      onChange={handleChange}
      exclusive={selectionMode === "single"}
      {...props}
    >
      {children}
    </MuiToggleButtonGroup>
  );
}
